import { useState, useRef, useEffect, useContext, Fragment } from "react";
import { Bot, SendHorizontal, Trash2 } from "lucide-react";
import { ThemeContext } from "./context/themeContext";
import { useNavigate } from "react-router-dom";
import "./AIDashboardChatbox.css";

function renderMarkdown(text) {
  if (!text) return null;
  return text.split("\n").map((line, li, arr) => {
    const tokens = line.split(/(\*\*[^*\n]+\*\*|\*[^*\n]+\*)/g);
    return (
      <Fragment key={li}>
        {tokens.map((tok, ti) => {
          if (tok.startsWith("**") && tok.endsWith("**"))
            return <strong key={ti}>{tok.slice(2, -2)}</strong>;
          if (tok.startsWith("*") && tok.endsWith("*"))
            return <em key={ti}>{tok.slice(1, -1)}</em>;
          return tok;
        })}
        {li < arr.length - 1 && <br />}
      </Fragment>
    );
  });
}

/* ── Groq config ────────────────────────────────────────── */
const GROQ_ENDPOINT = "https://api.groq.com/openai/v1/chat/completions";
const GROQ_MODEL    = "llama-3.3-70b-versatile";

/* ── Context-guarding system prompt ────────────────────── */
const SYSTEM_PROMPT = `You are MarsMate AI — a focused business intelligence assistant built into the MarsMate platform.

YOUR DOMAIN (answer ONLY questions related to these):
• Business idea validation and scoring
• Mind mapping and strategic business frameworks
• Go-to-market (GTM) strategy and execution planning
• Investor pitching, fundraising, and funding strategies
• Startup planning, MVP design, and growth models
• B2B business models, sales funnels, and partnerships
• Revenue models, pricing strategies, and monetization
• Market research, competitive analysis, and industry trends
• Product development, feature prioritization, and roadmaps
• Community building, membership models, and user retention
• Innovation, entrepreneurship, and business opportunity analysis
• Reports and insights generated within the MarsMate platform
• Features, navigation, and usage of the MarsMate platform itself

OUT OF SCOPE — If a question is NOT related to the topics above (e.g. celebrities, general trivia, personal advice, politics, unrelated science, coding not tied to a business idea, etc.), respond politely with:
"That's a bit outside my focus here! I'm specialised in business idea validation, mind mapping, GTM strategy, and helping you build ideas on MarsMate. Feel free to ask me anything in that space — I'm here to help!"

TONE — Be concise, professional, encouraging, and actionable. Avoid lengthy preambles. Get straight to the value.`;

/* ── Recommendation catalogue ───────────────────────────── */
const REC_CATALOGUE = [
  {
    id: "idea-validation",
    label: "Idea Validation",
    type: "mode",
    color: "#C1440E",
    path: "/idea-validation",
    tokenized: true,
    keywords: [
      "idea validation", "validate", "idea score", "validate my idea",
      "business idea", "feasibility", "market potential", "viable",
      "is my idea good", "test my idea", "idea analysis",
    ],
  },
  {
    id: "mind-mapping",
    label: "Mind Mapping",
    type: "mode",
    color: "#6366F1",
    path: "/mind-mapping",
    tokenized: true,
    keywords: [
      "mind map", "mind mapping", "strategy", "business hub", "framework",
      "roadmap", "plan", "mapping", "strategic", "structure",
    ],
  },
  {
    id: "gtm",
    label: "GTM",
    type: "category",
    color: "#10B981",
    path: "/idea-validation",
    tokenized: true,
    keywords: [
      "gtm", "go to market", "go-to-market", "market entry",
      "launch strategy", "channel strategy", "distribution", "customer acquisition",
    ],
  },
  {
    id: "investor",
    label: "Investor",
    type: "category",
    color: "#C1440E",
    path: "/idea-validation",
    tokenized: true,
    keywords: [
      "investor", "investment", "funding", "pitch", "fundraise",
      "venture capital", "vc", "angel investor", "seed round", "raise money",
    ],
  },
  {
    id: "b2b",
    label: "B2B",
    type: "category",
    color: "#3B82F6",
    path: "/idea-validation",
    tokenized: true,
    keywords: [
      "b2b", "business to business", "enterprise", "saas",
      "sales strategy", "lead generation", "pipeline", "b2b sales",
    ],
  },
  {
    id: "startup",
    label: "Startup",
    type: "category",
    color: "#EC4899",
    path: "/idea-validation",
    tokenized: true,
    keywords: [
      "startup", "start-up", "launch", "mvp", "bootstrap",
      "founder", "early stage", "pre-seed",
    ],
  },
  {
    id: "revenue",
    label: "Revenue",
    type: "category",
    color: "#F97316",
    path: "/mind-mapping",
    tokenized: true,
    keywords: [
      "revenue", "monetization", "pricing", "income", "profit",
      "business model", "recurring", "monetize",
    ],
  },
  {
    id: "community",
    label: "Community",
    type: "category",
    color: "#06B6D4",
    path: "/idea-validation",
    tokenized: true,
    keywords: [
      "community", "audience", "users", "engagement",
      "retention", "social", "community building",
    ],
  },
  {
    id: "membership",
    label: "Membership",
    type: "category",
    color: "#8B5CF6",
    path: "/idea-validation",
    tokenized: true,
    keywords: [
      "membership", "subscription", "recurring revenue",
      "members", "tier", "membership model",
    ],
  },
  {
    id: "reports",
    label: "View Reports",
    type: "mode",
    color: "#6366F1",
    path: "/reports-hub",
    tokenized: true,
    keywords: [
      "report", "reports", "my reports", "analysis results",
      "insights", "scorecard", "view results", "download report",
    ],
  },
];

function detectRecommendations(userText, aiText) {
  const combined = `${userText} ${aiText}`.toLowerCase();
  const seen     = new Set();
  const matches  = [];

  for (const rec of REC_CATALOGUE) {
    if (seen.has(rec.id)) continue;
    if (rec.keywords.some((kw) => combined.includes(kw))) {
      matches.push(rec);
      seen.add(rec.id);
    }
    if (matches.length >= 4) break;
  }
  return matches;
}

/* ── Component ──────────────────────────────────────────── */
export default function AIDashboardChatbox() {
  const { activeTheme } = useContext(ThemeContext);
  const isDark          = activeTheme === "dark";
  const navigate        = useNavigate();

  const [inputValue, setInputValue] = useState("");
  const [messages, setMessages]     = useState([]);
  const [isLoading, setIsLoading]   = useState(false);
  const [error, setError]           = useState(null);

  const textareaRef    = useRef(null);
  const messagesEndRef = useRef(null);

  // Auto-resize textarea
  useEffect(() => {
    const ta = textareaRef.current;
    if (!ta) return;
    ta.style.height = "auto";
    ta.style.height = `${Math.min(ta.scrollHeight, 160)}px`;
  }, [inputValue]);

  // Scroll to latest
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isLoading]);

  // Navigate helper (tokenized routes)
  const goTo = (path, tokenized) => {
    if (tokenized) {
      const a = crypto.randomUUID() + crypto.randomUUID();
      const b = crypto.randomUUID() + crypto.randomUUID();
      navigate(`${path}/${a}/${b}`);
    } else {
      navigate(path);
    }
  };

  const sendMessage = async (text) => {
    const trimmed = text.trim();
    if (!trimmed || isLoading) return;

    const userMsg = { id: Date.now(), role: "user", content: trimmed, ts: new Date() };
    const updated = [...messages, userMsg];
    setMessages(updated);
    setInputValue("");
    setError(null);
    setIsLoading(true);

    const apiKey = process.env.REACT_APP_GROQ_API_KEY;
    if (!apiKey) {
      setError("Groq API key not configured. Add REACT_APP_GROQ_API_KEY to your .env file.");
      setIsLoading(false);
      return;
    }

    try {
      const res = await fetch(GROQ_ENDPOINT, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${apiKey}`,
        },
        body: JSON.stringify({
          model: GROQ_MODEL,
          max_tokens: 1024,
          temperature: 0.65,
          messages: [
            { role: "system", content: SYSTEM_PROMPT },
            ...updated.map(({ role, content }) => ({ role, content })),
          ],
        }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err?.error?.message || `Groq API error ${res.status}`);
      }

      const data    = await res.json();
      const reply   = data.choices?.[0]?.message?.content || "No response received.";
      const recs    = detectRecommendations(trimmed, reply);

      setMessages((prev) => [
        ...prev,
        {
          id:              Date.now() + 1,
          role:            "assistant",
          content:         reply,
          ts:              new Date(),
          recommendations: recs,
        },
      ]);
    } catch (err) {
      setError(err.message || "Failed to get a response. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage(inputValue);
    }
  };

  const formatTime = (d) =>
    d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

  const hasChat = messages.length > 0 || isLoading || error;

  return (
    <div className={`ai-bar-wrapper ${isDark ? "dark-mode" : ""}`}>
      <div className="ai-bar">

        {/* ── Inline conversation ── */}
        {hasChat && (
          <div className="ai-inline-messages">
            {messages.map((msg) => (
              <div key={msg.id} className={`ai-msg-row ${msg.role}`}>
                {msg.role === "assistant" && (
                  <div className="ai-msg-avatar" aria-hidden="true">
                    <Bot size={13} />
                  </div>
                )}
                <div className="ai-msg-bubble-wrap">
                  <div className={`ai-msg-bubble ${msg.role}`}>
                    {msg.role === "assistant" ? renderMarkdown(msg.content) : msg.content}
                  </div>
                  <span className="ai-msg-time">{formatTime(msg.ts)}</span>

                  {/* ── Recommendation chips ── */}
                  {msg.role === "assistant" && msg.recommendations?.length > 0 && (
                    <div className="ai-rec-row">
                      <span className="ai-rec-hint">Jump to:</span>
                      {msg.recommendations.map((rec) => (
                        <button
                          key={rec.id}
                          className={`ai-rec-chip ai-rec-chip--${rec.type}`}
                          style={{ "--rec-color": rec.color }}
                          onClick={() => goTo(rec.path, rec.tokenized)}
                          type="button"
                        >
                          {rec.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}

            {isLoading && (
              <div className="ai-msg-row assistant">
                <div className="ai-msg-avatar" aria-hidden="true">
                  <Bot size={13} />
                </div>
                <div className="ai-msg-bubble-wrap">
                  <div className="ai-msg-bubble assistant ai-typing">
                    <span /><span /><span />
                  </div>
                </div>
              </div>
            )}

            {error && (
              <div className="ai-error-row">
                <span className="ai-error-text">{error}</span>
                <button
                  className="ai-error-retry"
                  type="button"
                  onClick={() => {
                    const last = [...messages].reverse().find((m) => m.role === "user");
                    if (last) { setError(null); sendMessage(last.content); }
                  }}
                >
                  Retry
                </button>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
        )}

        {/* ── Textarea ── */}
        <textarea
          ref={textareaRef}
          className="ai-bar-textarea"
          placeholder="Ask about idea validation, GTM strategy, mind mapping..."
          value={inputValue}
          rows={2}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          aria-label="MarsMate AI chat input"
        />

        {/* ── Bottom toolbar ── */}
        <div className="ai-bar-toolbar">
          <div className="ai-bar-toolbar-left">
            <span className="ai-bar-bot-badge" aria-hidden="true">
              <Bot size={15} />
              <span>MarsMate AI</span>
            </span>
          </div>
          <div className="ai-bar-toolbar-right">
            {messages.length > 0 && (
              <button
                className="ai-bar-clear"
                onClick={() => { setMessages([]); setError(null); }}
                title="Clear conversation"
                type="button"
              >
                <Trash2 size={14} />
              </button>
            )}
            <button
              className="ai-bar-send"
              onClick={() => sendMessage(inputValue)}
              disabled={!inputValue.trim() || isLoading}
              aria-label="Send message"
              type="button"
            >
              <SendHorizontal size={15} />
            </button>
          </div>
        </div>

      </div>
    </div>
  );
}
